import { ref, nextTick } from 'vue'

/**
 * useAgGridSidebar composable for AG Grid sidebar configuration and logic.
 * @param {Object} options
 * @param {Array} options.panels - Array of panel configs: [{ id, label, width }]
 * @param {Object} options.injectHeaders - Map of panel id to header text
 * @param {String} options.position - Sidebar position (default: 'left')
 * @param {Boolean} options.clearFilters - Whether to clear filters on initialization (default: false)
 * @returns {Object}
 */

export function useAgGridSidebar({ panels = [], injectHeaders = {}, position = 'left' } = {}) {
    const sideBar = ref({
        toolPanels: panels.map((panel) => ({
            id: panel.id,
            labelDefault: panel.label,
            labelKey: panel.id,
            iconKey: panel.id,
            toolPanel: panel.toolPanel || `ag${capitalize(panel.id)}ToolPanel`,
            width: panel.width,
            toolPanelParams: panel.toolPanelParams || undefined,
        })),
        position,
        hiddenByDefault: true,
    })

    const activeSideBarType = ref(null)
    const sideBarOpen = ref(false)

    // Inject custom headers into AG Grid sidebar panels
    const injectSidebarHeaders = () => {
        nextTick(() => {
            setTimeout(() => {
                Object.entries(injectHeaders).forEach(([panelId, headerText]) => {
                    let className, panelClass
                    if (panelId === 'filters') {
                        className = 'patent-filters-title'
                        panelClass = 'ag-filter-toolpanel'
                    } else if (panelId === 'columns') {
                        className = 'patent-columns-title'
                        panelClass = 'ag-column-panel'
                    } else {
                        className = `${panelId}-panel-title`
                        panelClass = `ag-${panelId}-toolpanel`
                    }
                    const panels = document.getElementsByClassName(panelClass)
                    if (panels.length > 0) {
                        const panel = panels[0]
                        if (!panel.querySelector(`h6.${className}`)) {
                            const h6 = document.createElement('h6')
                            h6.textContent = headerText
                            h6.className = `${className} ml-sm mt-md mb-lg`
                            panel.insertBefore(h6, panel.firstChild)
                        }
                    }
                })
            }, 100)
        })
    }

    // Toggle sidebar panel open/close
    const toggleSideBar = (type, gridApi) => {
        if (!gridApi) return
        const isCurrentlyVisible = gridApi.isSideBarVisible()
        const isSamePanel = activeSideBarType.value === type
        if (isCurrentlyVisible && isSamePanel) {
            gridApi.setSideBarVisible(false)
            activeSideBarType.value = null
            sideBarOpen.value = false
        } else {
            gridApi.setSideBarVisible(true)
            gridApi.openToolPanel(type)
            activeSideBarType.value = type
            sideBarOpen.value = true
        }
    }

    const clearFilters = (gridApi) => {
        if (!gridApi) return

        gridApi.setFilterModel(null)

        // Reset any column sorting that is applied
        const columnState = gridApi.getColumnState()
        if (Array.isArray(columnState) && columnState.length) {
            columnState.forEach((col) => {
                col.sort = null
                col.sortIndex = null
            })
            gridApi.applyColumnState({ state: columnState, defaultState: { sort: null } })
        }
    }

    return {
        sideBar,
        activeSideBarType,
        sideBarOpen,
        toggleSideBar,
        injectSidebarHeaders,
        clearFilters,
    }
}

function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1)
}
